import { useQuery } from '@tanstack/react-query';

import {
  type CompanyId,
  type Permissions,
  ResourceId,
  UserPermissions,
} from 'common/api/api-types';
import { deliveryOsGatewayApiAgent } from 'common/api/apiAgents';
import queryKeys from 'common/hooks/queryKeys';
import { decodeHttpResponse } from 'common/utils/apiUtils';

const fetchUserPermissions = ({ companyId }: { companyId: CompanyId }) =>
  decodeHttpResponse(
    UserPermissions,
    deliveryOsGatewayApiAgent.get(`/access-control/permissions/companies/${companyId}/user`),
  );

const useUserPermissions = ({ companyId }: { companyId?: CompanyId }) =>
  useQuery({
    queryKey: queryKeys.forUseUserPermissions({ companyId }),
    queryFn: () =>
      companyId ? fetchUserPermissions({ companyId }) : Promise.reject('Company ID is invalid'),
    select: ({ role, resourcePermissions }) => {
      const permissions: Permissions = {};

      const RESOURCE_IDS = ['earnings', 'role_management', 'scheduling.dashboard', 'city_health'];

      for (const resourceId of RESOURCE_IDS) {
        const resource = resourcePermissions?.find(r => r.resourceId === resourceId);
        if (resource?.permissions) {
          permissions[ResourceId.parse(resourceId)] = resource.permissions;
        }
      }

      return {
        role,
        permissions,
      };
    },
    enabled: Boolean(companyId),
  });

export default useUserPermissions;
